import { createAsyncThunk } from '@reduxjs/toolkit';
import { getPrivateProjectBankItemsOfProject } from 'scenes/ProjectBank/services/private-project-bank/getPrivateProjectBankItemsOfProject.service';
import { selectIsBankKeptToImages } from 'scenes/ProjectBank/store/selectors/project-bank-access.selectors';
import { isSystemAuthorId } from 'scenes/ProjectBank/utils/bankItem';
import { selectSelectedProjectId } from 'store/projects/selectors/projectsSelectors';
import { loadUsersAndPersonalResourcesByUserIds } from 'store/projectUsers/thunks/loadUsersAndPersonalResourcesByUserIds';
import { uniq } from 'lodash';

/**
 * Loads every private project bank item of the selected project and, as a
 * side effect, the users who created them (so the bank can show an author
 * for private items). The users request is skipped for an empty id list
 * because the endpoint rejects `[]`.
 *
 * Not while the bank is kept to its images folder (a gallery-only user,
 * utils/bankAccess, or the bank open as the project's gallery): the private
 * bank is no part of the gallery, so nothing is asked. On a fresh page load
 * the bank may start before the permissions tell who the user is; the
 * request goes out then, and should it fail its rejection is left quiet —
 * nothing waits on it and no notice is shown.
 */
export const loadPrivateProjectBankItemsOfProject = createAsyncThunk(
  'project-bank/loadPrivateProjectBankItemsOfProject',
  async (_, { getState, dispatch, rejectWithValue }) => {
    const projectId = selectSelectedProjectId(getState());

    const privateProjectBankItems =
      await getPrivateProjectBankItemsOfProject(projectId);

    // An answer for a project the user has since left (a quick hop from one
    // bank to another) is not this bank's.
    if (selectSelectedProjectId(getState()) !== projectId) {
      return rejectWithValue({ stale: true, projectId });
    }

    // The AI's own items (`SYSTEM:` authors) belong to no user to load.
    const creatorIds = uniq(
      (privateProjectBankItems || [])
        .map((item) => item.createdBy)
        .filter((id) => id && !isSystemAuthorId(id))
    );

    if (creatorIds.length > 0) {
      dispatch(loadUsersAndPersonalResourcesByUserIds(creatorIds));
    }

    return privateProjectBankItems;
  },
  {
    condition: (_, { getState }) => !selectIsBankKeptToImages(getState()),
  }
);
